import Path from 'models/path';
import Link from 'models/link';
import { canopyContainer } from 'helpers/getters';
import ScrollableContainer from 'helpers/scrollable_container';
import Topic from '../../cli/shared/topic';

class Paragraph {
  // A paragraph instance represents a visible paragraph, not
  // specifically the DOM element of type paragraph.
  //
  // Visible paragraphs in the UI are created with a section element
  // that has a paragraph element child.
  //
  // paragraph.sectionElement returns the section element, whereas
  // paragraph.paragraphElement returns the paragraph element.

  constructor(sectionElement) {
    if (!sectionElement || sectionElement.tagName !== 'SECTION' ) throw new Error("Paragraph instantiation requires section element");
    if (!sectionElement.classList.contains('canopy-section')) throw new Error("Paragraph class requires Canopy section element");

    const path = sectionElement.dataset.pathString;
    let cachedParagraph = Paragraph.byPath(path);
    if (cachedParagraph && cachedParagraph.sectionElement !== sectionElement) throw new Error(`Multiple DOM objects instantiated for single sectionElement`);
    if (cachedParagraph) return cachedParagraph;

    this.sectionElement = sectionElement;
    this.transferDataset();
  }

  static from(sectionElement) {
    return new this(sectionElement);
  }

  equals(otherParagraph) {
    if (!otherParagraph) return false;
    return this.sectionElement === otherParagraph.sectionElement;
  }

  allocateSpace() {
    this.sectionElement.style.display = 'block';
    this.sectionElement.style.opacity = '0%';
  }

  display() {
    this.sectionElement.style.display = 'block';
    this.sectionElement.style.opacity = '100%'; // prevents early webkit playwright spec bug before fix

    // Fixes firefox unicode bug
    let paragraphElement = this.paragraphElement;
    if (!paragraphElement) return;

    paragraphElement.style.unicodeBidi = 'unset';
    requestAnimationFrame(() => {
      paragraphElement.style.unicodeBidi = 'plaintext';
    });
  }

  get element() {
    throw new Error("Depreciated in favor of #sectionElement property");
  }

  transferDataset() { // This is so we can more easily debug in the console
    this._topicName = this.sectionElement.dataset.topicName;
    this._subtopicName = this.sectionElement.dataset.subtopicName;
    this._pathString = this.sectionElement.dataset.pathString;
    this.pathDepth = Number(this.sectionElement.dataset.pathDepth);
  }

  get isVisible() {
    return window.getComputedStyle(this.sectionElement).display !== 'none';
  }

  // Visual loading state: content may already exist, but remains hidden until displayPath clears this class.
  get loadingElementVisible() {
    return this.sectionElement.classList.contains('canopy-loading-section');
  }

  // Placeholder state: registered shell whose paragraph content has not been rendered yet.
  get placeholder() {
    return this.loadingElementVisible && !this.paragraphElement;
  }

  get loadingElement() {
    return this.sectionElement.querySelector(':scope > .canopy-loading-graphic');
  }

  get contentElement() {
    return this.paragraphElement || this.loadingElement;
  }

  get isFocused() {
    const rect = this.contentElement.getBoundingClientRect();
    const viewportHeight = ScrollableContainer.visibleHeight;

    const topLimit = viewportHeight * 0.1;
    const bottomLimit = viewportHeight * 0.5;

    return rect.top > topLimit && rect.top < bottomLimit;
  }

  get topic () {
    return Topic.fromMixedCase(this.sectionElement.dataset.topicName);
  }

  get subtopic () {
    return Topic.fromMixedCase(this.sectionElement.dataset.subtopicName);
  }

  get topicName() {
    return this.topic.mixedCase;
  }

  get subtopicName() {
    return this.subtopic.mixedCase;
  }

  get paragraphElement() {
    let paragraphElement = Array.from(this.sectionElement.childNodes).
      find((element) => element.tagName === 'P');

    return paragraphElement || null;
  }

  get path() {
    return new Path(this.sectionElement.dataset.pathString);
  }

  get literalPath() {
    return Path.forSegment(this.topic, this.subtopic);
  }

  get topicPath() {
    return this.path.lastSegment.firstTopicPath;
  }

  get pathDown() { // path from the paragraph down the page
    return this.path.slice(this.pathDepth);
  }

  get links() {
    let paragraphElement = this.paragraphElement;
    if (!paragraphElement) return [];

    let linkElements = paragraphElement.querySelectorAll('a.canopy-selectable-link');
    return Array.from(linkElements).map((element) => new Link(element));
  }

  get simpleGlobalLinks() {
    return this.links.filter(l => l.isSimpleGlobal);
  }

  get pathReferenceLinks() {
    return this.links.filter(l => l.isPathReference);
  }

  openPathReferenceLinksFor(currentPath) {
    return this.pathReferenceLinks.filter(l =>
      l.isOpenPathReferenceFor(currentPath, this.path)
    );
  }

  get linkElements() {
    let paragraphElement = this.paragraphElement;
    if (!paragraphElement) return [];

    return Array.from(paragraphElement.querySelectorAll('.canopy-selectable-link'));
  }

  get firstLink() {
    return this.links[0] || null;
  }

  nthLink(n) {
    return this.links[n] || null;
  }

  get lastLink() {
    return this.links[this.links.length - 1] || null;
  }

  get previousLink() {
    return Link.lastSelectionOfParagraph(this);
  }

  get isPageRoot() {
    return this.parentNode === canopyContainer
  }

  get hasLinks() {
    return this.links.length > 0;
  }

  linkBySelector(callback) {
    return this.links.find(callback);
  }

  linksBySelector(callback) {
    return this.links.filter(callback);
  }

  linkByChild(givenTopic, givenSubtopic) {
    return this.linkBySelector(
      (link) =>
        (link.isGlobal && link.childTopic.caps === givenTopic?.caps) ||
        (link.isLocal && link.targetSubtopic.caps === givenSubtopic?.caps)
    );
  }

  get parentLinkCandidates() {
    return this.parentParagraph.linksBySelector(
      (link) =>
        (link.isGlobal && link.childTopic.caps === this.topic.caps) ||
        (link.isLocal && link.targetSubtopic.caps === this.subtopic.caps));

  }

  get parentLinks() {
    if (this.isPageRoot) { return null; }

    const parentParagraph = this.parentParagraph;
    if (!parentParagraph) return null; // safety

    // Get all candidate parent links
    const parentLinkCandidates = this.parentLinkCandidates;

    // if paragraph is subtopic, parent link must be local reference in parent - return all to allow multiple parent links
    if (!this.path.lastSegment.isSingleTopic) {
      return parentParagraph.links.filter(link => link.isLocal && Topic.areEqual(link.targetSubtopic, this.subtopic));
    }

    // If any open path references, put them first, then everything else
    const openPathParents = parentLinkCandidates.filter(link => link.isOpenPathReference);
    if (openPathParents.length) {
      return [...openPathParents]; // when there is an open path reference, we only return that
    }

    // If simple globals exist: use only them, ignore closed path refs
    const simpleGlobals = parentParagraph.simpleGlobalLinks.filter(link =>
      Topic.areEqual(link.childTopic, this.path.lastSegment.firstTopic)
    );
    if (simpleGlobals.length) {
      return simpleGlobals;
    }

    // If open parent is also selected link, put it first but return all parents
    if (parentParagraph.linkElements.includes(Link.selection?.element) && Link.selection?.childParagraph?.equals(this)) {
      return [Link.selection, ...parentLinkCandidates.filter(link => !link.equals(Link.selection))];
    }

    // if there are multiple global links beginning with the given topic, prefer the last selection (instead of returning all for simplicity)
    let lastSelectionOfParent = Link.lastSelectionOfParagraph(parentParagraph);
    if (parentLinkCandidates.length > 0 && lastSelectionOfParent && parentLinkCandidates.find(l => l.equals(lastSelectionOfParent))) {
      return [lastSelectionOfParent, ...parentLinkCandidates.filter(l => !l.equals(lastSelectionOfParent))];
    }

    // otherwise return all results
    return parentLinkCandidates;
  }

  get parentLink() {
    return this.parentLinks?.[0];
  }

  get displayTopicName() {
    return this.sectionElement.dataset.displayTopicName;
  }

  get parentParagraph() {
    if (!this.parentNode || this.parentNode.tagName !== 'SECTION') { // we require this.parentNode so don't search the DOM
      return null;
    }
    return Paragraph.for(this.parentNode);
  }

  get topicParagraph() {
    if (this.isSingleTopic) return this;

    const topicSection = this.sectionElement.parentNode.closest('.canopy-topic-section');
    if (!topicSection) throw new Error('Missing topic paragraph');
    return Paragraph.for(topicSection);
  }

  get isSingleTopic() {
    return this.topic.equals(this.subtopic);
  }

  get isInDom() {
    return canopyContainer.contains(this.sectionElement);
  }

  get isBig() {
    let paragraphPercent = this.contentElement.offsetHeight / ScrollableContainer.visibleHeight;
    return paragraphPercent > .75;
  }

  get fits() {
    let paragraphPercent = this.contentElement.offsetHeight / ScrollableContainer.visibleHeight;
    return paragraphPercent < .8;
  }

  get top() {
    return this.contentElement.getBoundingClientRect().top;
  }

  get bottom() {
    return this.contentElement.getBoundingClientRect().bottom;
  }

  get positionOnViewport() {
    const rect = this.contentElement.getBoundingClientRect();
    const viewportHeight = ScrollableContainer.visibleHeight;
    const top = rect.top;

    if (top < 0) return -1;
    if (top > viewportHeight) return Infinity;
    return (top / viewportHeight) * 100;
  }

  static selectedParagraphObject = null;

  static get selection() {
    return Paragraph.selectedParagraphObject || Paragraph.current;
  }

  static get current() {
    const sectionElement = document.querySelector('.canopy-selected-section');
    return sectionElement ? Paragraph.for(sectionElement) : null;
  }

  addSelectionClass() {
    this.sectionElement.classList.add('canopy-selected-section');
    Paragraph.selectedParagraphObject = this;
  }

  removeSelectionClass() {
    this.sectionElement.classList.remove('canopy-selected-section');
    if (Paragraph.selectedParagraphObject?.equals(this)) Paragraph.selectedParagraphObject = null;
  }

  scrollComplete() {
    this.sectionElement.classList.add('canopy-scroll-complete');
  }

  static get root() {
    let path = Path.current.firstTopicPath;
    return path.paragraph;
  }

  static containingLink(link) {
    if (!(link instanceof Link)) throw new Error("Must provide link instance argument");
    const sectionElement = link.element.parentNode.closest('.canopy-section');
    return Paragraph.for(sectionElement);
  }

  static get visible() {
    let path = Path.rendered;
    let paragraphs = [];

    while (path) {
      paragraphs.push(path.paragraph);
      path = path.parentPath;
    }

    return paragraphs;
  }

  static atY(integerY) {
    return Paragraph.visible.reduce((bestParagraph, currentParagraph) => {
      if (bestParagraph.top < integerY && bestParagraph.bottom > integerY) return bestParagraph;
      if (currentParagraph.top < integerY && currentParagraph.bottom > integerY) return currentParagraph;
      return Math.abs(bestParagraph.top - integerY) < Math.abs(currentParagraph.top - integerY) ? bestParagraph : currentParagraph;
    });
  }

  // Shadow DOM
  static paragraphsByPath = {};
  static byPath(path) {
    if (!path) return null;

    const pathObject = path instanceof Path ? path : null;
    const pathString = pathObject ? pathObject.pathString : path;

    let paragraph = this.paragraphsByPath[pathString];
    if (!paragraph && pathObject) paragraph = this.paragraphsByPath[pathObject.recapitalize.pathString];

    if (!paragraph?.parentNode) return null; // cache functions haven't run
    return paragraph;
  }

  removeFromDom() {
    this.sectionElement.parentNode?.removeChild(this.sectionElement);
  }

  unregister() {
    this.removeFromDom();
    delete Paragraph.paragraphsByPath[this.path.string];
  }

  static unregisterTree(rootSectionElement) {
    Paragraph.sectionElementsUnder(rootSectionElement).reverse().forEach(sectionElement => {
      const paragraph = Paragraph.paragraphsByPath[sectionElement.dataset.pathString];
      if (paragraph?.sectionElement === sectionElement) paragraph.unregister();
    });
  }

  addToDom() {
    if (!this.parentNode) {
      throw new Error('sectionElement missing parentNode');
    }

    if (this.parentParagraph) {
      this.parentParagraph.addToDom(); // ensure parent first
    }

    if (this.sectionElement.parentNode !== this.parentNode) {
      this.parentNode.appendChild(this.sectionElement);
    }
  }

  executePreDisplayCallbacks() {
    if (this.placeholder) return; // wait for content to render
    if (!this.contentElement?.getClientRects().length) return; // wait until layout is measurable

    const callbacks = this.sectionElement.preDisplayCallbacks || [];
    callbacks.forEach(callback => callback());
    this.sectionElement.preDisplayCallbacks = [];
  }

  valid() { // to avoid errors with temp sections appended for styling info
    return !!this.dataset.topicName;
  }

  static registerChild(childElement, parentElement) {
    const childParagraph = Paragraph.registerNode(childElement);
    childParagraph.parentNode = parentElement; // including canopyContainer
    return childParagraph;
  }

  static registerNode(sectionElement) {
    const path = new Path(sectionElement.dataset.pathString).recapitalize; // use canonical capitalizations in case URL version is wrong
    const existing = Paragraph.byPath(path);
    if (existing && existing.sectionElement !== sectionElement) {
      throw new Error(`Multiple DOM objects instantiated for path ${path.string}`);
    }

    const paragraph = new Paragraph(sectionElement);
    sectionElement.dataset.pathString = path.string; // persist recapitalized path string
    this.paragraphsByPath[path.string] = paragraph;
    paragraph.transferDataset();
    return paragraph;
  }

  static executePreDisplayCallbacksTree(rootSectionElement, options = {}) {
    if (!Paragraph.contentLoaded) return Promise.resolve(); // pre-running callbacks is optimization except on initial load

    const sectionElements = Paragraph.sectionElementsUnder(rootSectionElement);
    const initiallyConnected = new Map(sectionElements.map(sectionElement => [sectionElement, sectionElement.isConnected]));

    if (options.eager) {
      return Paragraph.executePreDisplayCallbacksTreeEager(rootSectionElement, sectionElements, initiallyConnected);
    }

    sectionElements.forEach(sectionElement => Paragraph.executePreDisplayCallbacksForSection(sectionElement));
    Paragraph.cleanupPreDisplayCallbacksTree(rootSectionElement, sectionElements, initiallyConnected);
    return Promise.resolve();
  }

  static executePreDisplayCallbacksTreeEager(rootSectionElement, sectionElements, initiallyConnected) {
    const runSection = index => {
      const sectionElement = sectionElements[index];
      if (!sectionElement) {
        Paragraph.cleanupPreDisplayCallbacksTree(rootSectionElement, sectionElements, initiallyConnected);
        return Promise.resolve();
      }

      return Paragraph.scheduleEagerPreDisplayCallback(() => {
        Paragraph.executePreDisplayCallbacksForSection(sectionElement);
      }).then(() => runSection(index + 1));
    };

    return runSection(0).catch(error => {
      Paragraph.cleanupPreDisplayCallbacksTree(rootSectionElement, sectionElements, initiallyConnected);
      throw error;
    });
  }

  static executePreDisplayCallbacksForSection(sectionElement) {
    const paragraph = Paragraph.for(sectionElement);
    if (!sectionElement.isConnected) paragraph.addToDom();

    sectionElement.style.display = 'block';
    paragraph.executePreDisplayCallbacks();
    sectionElement.style.removeProperty('display');
  }

  static scheduleEagerPreDisplayCallback(callback) {
    return new Promise((resolve, reject) => {
      const run = () => {
        try {
          callback();
          resolve();
        } catch (error) {
          reject(error);
        }
      };

      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(run);
      } else {
        setTimeout(run);
      }
    });
  }

  static sectionElementsUnder(rootSectionElement) {
    const sectionElements = [rootSectionElement];
    const seenElements = new Set(sectionElements);

    for (let index = 0; index < sectionElements.length; index++) {
      const parentElement = sectionElements[index];

      Object.values(Paragraph.paragraphsByPath).forEach(paragraph => {
        const childElement = paragraph.sectionElement;
        if (paragraph.parentNode !== parentElement) return;
        if (seenElements.has(childElement)) return;

        seenElements.add(childElement);
        sectionElements.push(childElement);
      });
    }

    return sectionElements;
  }

  static cleanupPreDisplayCallbacksTree(rootSectionElement, sectionElements, initiallyConnected) {
    for (let index = sectionElements.length - 1; index > 0; index--) {
      const sectionElement = sectionElements[index];
      if (initiallyConnected.get(sectionElement)) continue;
      sectionElement.parentNode?.removeChild(sectionElement);
    }

    if (!initiallyConnected.get(rootSectionElement)) {
      rootSectionElement.parentNode?.removeChild(rootSectionElement);
    }
  }

  static executePreDisplayCallbacks(sectionElement) {
    sectionElement.dataset.decorated = true;
    let paragraph = Paragraph.for(sectionElement);
    canopyContainer.appendChild(paragraph.sectionElement);
    sectionElement.style.display = 'block';
    paragraph.executePreDisplayCallbacks();
    sectionElement.style.removeProperty('display');
    canopyContainer.removeChild(paragraph.sectionElement);
  }

  static get all() {
    return Array.from(document.querySelectorAll('.canopy-section')).map(sectionElement => Paragraph.for(sectionElement));
  }

  static get focused() {
    return Paragraph.atY(ScrollableContainer.visibleHeight * 0.3 + ScrollableContainer.currentScroll);
  }

  static get contentLoaded() {
    return !!(document.querySelector('h1.canopy-header') && document.querySelector('section.canopy-section')); // this is a proxy for whether the first render has occured yet
  }

  static enableDisplayInProgress() {
    canopyContainer.dataset.displayInProgress = 'true';
  }

  static disableDisplayInProgress() {
    canopyContainer.dataset.displayInProgress = 'false';
  }

  static get displayInProgress() {
    return canopyContainer.dataset.displayInProgress === 'true';
  }

  static for(element) {
    return new Paragraph(element); // handles caching logic
  }
}

export default Paragraph;
